import { Avatar, Badge, Button, DropdownMenu } from '@components'
import { PermissionBoundary } from '@context'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaEllipsisH, FaEnvelope, FaPen, FaTrashAlt } from 'react-icons/fa'
import styled from 'styled-components'
import { PeopleTableDataRow, PersonCell, StatusCell, ActionsCell } from './PeopleTable.styled'
import { PeopleTableRow as PeopleTableRowModel } from './peopleTableUtils'

interface Props {
  row: PeopleTableRowModel
  removeDisabled?: boolean
  removeDisabledTooltip?: string
  onEdit?: () => void
  onRemove: () => void
}

export const PeopleTableRow: React.FC<Props> = ({
  row,
  removeDisabled = false,
  removeDisabledTooltip,
  onEdit,
  onRemove
}) => {
  const [t] = useTranslation()
  const isMember = row.kind === 'member'
  const statusLabel = isMember
    ? t('settings.people.status.member')
    : t('settings.people.status.invited')

  const menuItems = [
    ...(isMember && onEdit
      ? [
          <Button key="edit" type="button" isSecondary={true} onClick={onEdit} icon={<FaPen />}>
            {t('settings.people.edit')}
          </Button>
        ]
      : []),
    <Button
      key="remove"
      type="button"
      intent="danger"
      isSecondary={true}
      disabled={removeDisabled}
      title={removeDisabled ? removeDisabledTooltip : undefined}
      onClick={() => {
        if (!removeDisabled) onRemove()
      }}
      icon={<FaTrashAlt />}
    >
      {t('settings.people.remove')}
    </Button>
  ]

  return (
    <PeopleTableDataRow>
      <PersonCell>
        {isMember ? (
          <Avatar
            src={row.avatarUrl}
            size={36}
            permissions={row.usership.permissions}
            name={row.primary}
          />
        ) : (
          <InviteAvatar aria-hidden>
            <FaEnvelope />
          </InviteAvatar>
        )}
        <PersonText>
          <PrimaryText title={row.primary}>{row.primary}</PrimaryText>
          {row.secondary && <SecondaryText title={row.secondary}>{row.secondary}</SecondaryText>}
        </PersonText>
      </PersonCell>

      <div className="col-role">
        <Badge soft type={row.roleBadgeType}>
          {row.roleLabel}
        </Badge>
      </div>

      <StatusCell $tone={isMember ? 'member' : 'invited'}>
        <StatusDot $tone={isMember ? 'member' : 'invited'} />
        <span>{statusLabel}</span>
      </StatusCell>

      <ActionsCell>
        <PermissionBoundary>
          <DropdownMenu items={menuItems}>
            <Button type="button" isSecondary={true} icon={<FaEllipsisH />} />
          </DropdownMenu>
        </PermissionBoundary>
      </ActionsCell>
    </PeopleTableDataRow>
  )
}

const InviteAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px dashed ${({ theme }) => theme.colors.iconGray};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  flex-shrink: 0;
`

const PersonText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`

const PrimaryText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SecondaryText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StatusDot = styled.span<{ $tone: 'member' | 'invited' }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme, $tone }) =>
    $tone === 'member' ? theme.colors.success : theme.colors.warning};
`
